import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { gsap } from 'gsap'
import './Header.scss'

const Header = ({ showSlider, onCloseSlider, pageTitle = "page title here", isArtworkSlider = false, isCommissionSlider = false, isEventSlider = false, isPortraitsPage = false, isPortraits02Slider = false, isPortraitsModalOpen = false, isArtworkModalOpen = false, isCommissionModalOpen = false, isEventModalOpen = false, onClosePortraitsModal = null, buttons = null }) => {
  const btn = buttons?.fields || {};
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const overlayRef = useRef(null);
  const menuRef = useRef(null);
  const pageTitleRef = useRef(null);
  const dashRef = useRef(null);
  const alexTextRef = useRef(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const scrollToBiography = () => {
    console.log('scrollToBiography called');
    // Close menu first
    setIsMenuOpen(false);

    // Force navigation to home page with hash
    console.log('Navigating to /#bio');
    window.location.href = '/#bio';
  };

  // Initialize overlay and menu as hidden
  useEffect(() => {
    if (overlayRef.current && menuRef.current) {
      gsap.set(overlayRef.current, { autoAlpha: 0 });
      gsap.set(menuRef.current, { autoAlpha: 0, y: -20 });
    }
  }, []);

  // GSAP animations for menu overlay and modal
  useEffect(() => {
    const isModalOpen = isPortraitsModalOpen || isArtworkModalOpen || isCommissionModalOpen || isEventModalOpen;

    if (isMenuOpen) {
      // Fade in overlay
      gsap.fromTo(overlayRef.current,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.2, ease: "power2.out" }
      );

      // Fade in menu items
      gsap.fromTo(menuRef.current,
        { autoAlpha: 0, y: -20 },
        { autoAlpha: 1, y: 0, duration: 0.25, ease: "power2.out", delay: 0 }
      );

      // Hide page title, dash, and menu
      gsap.to([pageTitleRef.current, dashRef.current, ".header-menu"],
        { autoAlpha: 0, duration: 0.1, ease: "power2.in" }
      );

      // Adjust color: transparent for close icon (modal open), white for Alex text
      gsap.to(alexTextRef.current,
        { color: isModalOpen ? "transparent" : "white", duration: 0.2, ease: "power2.out" }
      );
    } else {
      // Always fade out overlay when menu closes
      gsap.to(overlayRef.current,
        { autoAlpha: 0, duration: 0.2, ease: "power2.in" }
      );

      // Fade out menu items
      gsap.to(menuRef.current,
        { autoAlpha: 0, y: -20, duration: 0.2, ease: "power2.in" }
      );

      if (isModalOpen) {
        // Hide label when modal is open, keep menu visible
        gsap.to([pageTitleRef.current, dashRef.current],
          { autoAlpha: 0, duration: 0.2, ease: "power2.in" }
        );
        gsap.to(".header-menu",
          { autoAlpha: 1, duration: 0.2, ease: "power2.out" }
        );
      } else {
        // Show page title, dash, and menu when no modal is open
        gsap.to([pageTitleRef.current, dashRef.current, ".header-menu"],
          { autoAlpha: 1, duration: 0.2, ease: "power2.out" }
        );
      }

      // Restore color: black for close icon (modal open), inherit for Alex text
      gsap.to(alexTextRef.current,
        { color: isModalOpen ? "black" : "inherit", duration: 0.2, ease: "power2.out" }
      );
    }
  }, [isMenuOpen, isPortraitsModalOpen, isArtworkModalOpen, isCommissionModalOpen, isEventModalOpen]);

  return (
    <>
      <div className="imprint" style={{ visibility: isMenuOpen ? 'visible' : 'hidden' }}>
        <Link to="/imprint" className="imprint-link">Imprint</Link>
        {/*    <p>Privacy Policy</p> */}
      </div>

      <div className={`menu-overlay ${isMenuOpen ? 'open' : ''}`} ref={overlayRef} onClick={toggleMenu}></div>

      <header className="header">

        {(isPortraitsModalOpen || isCommissionModalOpen || isArtworkModalOpen) ? (
          <div
            className="header-close-icon"
            onClick={isPortraitsModalOpen && onClosePortraitsModal ? onClosePortraitsModal : onCloseSlider}
            ref={alexTextRef}
          >
            <svg width="20" height="20" viewBox="5 5 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
            </svg>
          </div>
        ) : (
          <Link to="/" className="header-alex-pages" ref={alexTextRef}><p>Alex de Brabant</p></Link>
        )}

        <div className="menu-container">

          <div className={`menu-container-right ${isMenuOpen ? 'menu-open' : ''}`}>
            {isArtworkSlider ? (
              <Link to="/artworks" className="page-title-link" ref={pageTitleRef}>
                <p>Artworks</p>
              </Link>
            ) : isCommissionSlider ? (
              <Link to="/commissions" className="page-title-link" ref={pageTitleRef}>
                <p>Commissions</p>
              </Link>
            ) : isPortraits02Slider ? (
              <Link to="/portraits-02" className="page-title-link" ref={pageTitleRef}>
                <p>Portraits</p>
              </Link>
            ) : isEventSlider ? (
              <Link to="/events" className="page-title-link" ref={pageTitleRef}>
                <p>Events</p>
              </Link>
            ) : isPortraitsPage ? (
              isPortraitsModalOpen ? (
                <div className="page-title-link" ref={pageTitleRef} onClick={onClosePortraitsModal}>
                  <p>Portraits</p>
                </div>
              ) : (
                <Link to="/portraits" className="page-title-link portraits-page" ref={pageTitleRef}>
                  <p>Portraits</p>
                </Link>
              )
            ) : (
              <p className="page-title" ref={pageTitleRef}>{pageTitle}</p>
            )}
            <p ref={dashRef} className="dash">•</p>
            <p className="header-menu" onClick={toggleMenu}>Menu</p>
          </div>

          <div className="vertical-menu" ref={menuRef} style={{ visibility: isMenuOpen ? 'visible' : 'hidden' }}>
            {btn.artworks !== false && <Link to="/artworks" className="menu-item"><p>Artworks</p></Link>}
            {btn.portraits !== false && <Link to="/portraits" className="menu-item"><p>Portraits</p></Link>}
            {btn.commissions !== false && <Link to="/commissions" className="menu-item"><p>Commissions</p></Link>}
            {btn.events === true && <Link to="/events" className="menu-item"><p>Events</p></Link>}
            <Link className="menu-item" onClick={scrollToBiography}><p>Biography</p></Link>
            {btn.shop === true && (
              <a
                href="https://www.alexdebrabanteditions.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="menu-item"
              >
                <p>Shop</p>
              </a>
            )}
            <Link to="/info" className="menu-item"><p>Info</p></Link>
          </div>
        </div>
      </header>
    </>
  )
}

export default Header